/**
 * Map utility functions for fleet map functionality
 */

import { arrayUtils, mathUtils } from "./utils.js";
import { getStatusColorHex, getStatusPriority } from "./status-utils.js";

/**
 * Analyzes data distribution to determine optimal clustering strategy
 * @param {Array} compressors - Array of compressor data with coordinates
 * @returns {Object} Data analysis results
 */
export const analyzeDataDistribution = (compressors) => {
  if (!compressors || arrayUtils.isEmpty(compressors)) {
    return {
      count: 0,
      spread: "none",
      density: "low",
      clusteringThreshold: 14,
      maxZoom: 15,
      padding: [20, 20],
    };
  }

  const coords = compressors.map((c) => [c.lat, c.lng]);
  const lats = coords.map((c) => c[0]);
  const lngs = coords.map((c) => c[1]);

  const latSpan = mathUtils.max(...lats) - mathUtils.min(...lats);
  const lngSpan = mathUtils.max(...lngs) - mathUtils.min(...lngs);
  const maxSpan = mathUtils.max(latSpan, lngSpan);

  // Determine data characteristics
  let spread, density, clusteringThreshold, maxZoom, padding;

  if (maxSpan > 10) {
    spread = "very-wide";
    density = "very-low";
    clusteringThreshold = 15;
    maxZoom = 12;
    padding = [40, 40];
  } else if (maxSpan > 5) {
    spread = "wide";
    density = "low";
    clusteringThreshold = 16;
    maxZoom = 14;
    padding = [30, 30];
  } else if (maxSpan > 2) {
    spread = "medium";
    density = "medium";
    clusteringThreshold = 17;
    maxZoom = 16;
    padding = [25, 25];
  } else if (maxSpan > 0.5) {
    spread = "tight";
    density = "high";
    clusteringThreshold = 18;
    maxZoom = 18;
    padding = [20, 20];
  } else {
    spread = "very-tight";
    density = "very-high";
    clusteringThreshold = 19;
    maxZoom = 20;
    padding = [15, 15];
  }

  return {
    count: compressors.length,
    spread,
    density,
    clusteringThreshold,
    maxZoom,
    padding,
    latSpan,
    lngSpan,
    maxSpan,
  };
};

/**
 * Gets clustering threshold based on zoom level
 * @param {number} zoom - Current zoom level
 * @returns {number} Clustering threshold in degrees
 */
export const getClusteringThreshold = (zoom) => {
  if (zoom <= 5) return 1.0; // Very wide view - large clusters
  if (zoom <= 7) return 0.5; // Wide view - medium clusters
  if (zoom <= 9) return 0.2; // Medium view - smaller clusters
  if (zoom <= 11) return 0.1; // Close view - small clusters
  if (zoom <= 13) return 0.05; // Very close view - tiny clusters
  return 0.02; // Minimal clustering for building view
};

/**
 * Gets data-dependent clustering threshold
 * @param {number} zoom - Current zoom level
 * @param {Object} dataAnalysis - Data analysis results
 * @returns {number} Clustering threshold in degrees
 */
export const getDataDependentThreshold = (zoom, dataAnalysis) => {
  const baseThreshold = getClusteringThreshold(zoom);

  // Adjust threshold based on data characteristics
  switch (dataAnalysis.density) {
    case "very-high":
      return baseThreshold * 0.5; // Tighter clustering for dense data
    case "high":
      return baseThreshold * 0.7;
    case "medium":
      return baseThreshold; // Standard threshold
    case "low":
      return baseThreshold * 1.5; // Looser clustering for sparse data
    case "very-low":
      return baseThreshold * 2.0; // Much looser clustering for very sparse data
    default:
      return baseThreshold;
  }
};

/**
 * Determines if clustering should be disabled based on zoom and data analysis
 * @param {number} currentZoom - Current zoom level
 * @param {Object} dataAnalysis - Data analysis results
 * @returns {boolean} Whether to disable clustering
 */
export const shouldDisableClustering = (currentZoom, dataAnalysis) => {
  // Disable clustering at zoom level 13 and above
  return currentZoom >= 13;
};

/**
 * Calculates optimal bounds based on data characteristics
 * @param {Array} clusters - Array of clusters
 * @param {Object} dataAnalysis - Data analysis results
 * @returns {Object} Leaflet bounds object
 */
export const calculateOptimalBounds = (clusters, dataAnalysis) => {
  const L = window.L;
  const bounds = L.latLngBounds();

  clusters.forEach((cluster) => {
    bounds.extend(cluster.center);
  });

  // Add extra padding for very spread out data
  if (dataAnalysis.spread === "very-wide" || dataAnalysis.spread === "wide") {
    const latPadding = dataAnalysis.latSpan * 0.1;
    const lngPadding = dataAnalysis.lngSpan * 0.1;
    bounds.pad(latPadding, lngPadding);
  }

  return bounds;
};

/**
 * Gets fit bounds options based on data analysis
 * @param {Object} dataAnalysis - Data analysis results
 * @returns {Object} Fit bounds options
 */
export const getFitBoundsOptions = (dataAnalysis) => {
  return {
    padding: dataAnalysis.padding,
    maxZoom: dataAnalysis.maxZoom,
    animate: dataAnalysis.count < 50, // Only animate for smaller datasets
  };
};

/**
 * Creates a custom div icon for different compressor statuses
 * @param {string} status - The compressor status
 * @returns {Object|null} Leaflet divIcon instance or null if Leaflet not loaded
 */
export const createStatusIcon = (status) => {
  if (!window.L) return null;

  const L = window.L;
  const color = getStatusColorHex(status);

  return L.divIcon({
    html: `<div style="
      background-color: ${color};
      width: 12px;
      height: 12px;
      border-radius: 50%;
      border: 2px solid white;
      box-shadow: 0 2px 4px rgba(0,0,0,0.3);
      transition: all 0.3s ease-in-out;
      transform-origin: center;
    "></div>`,
    className: "custom-marker",
    iconSize: [12, 12],
    iconAnchor: [6, 6],
  });
};

/**
 * Creates a cluster icon with count
 * @param {number} count - Number of compressors in cluster
 * @param {string} status - Primary status of cluster
 * @returns {Object|null} Leaflet divIcon instance or null if Leaflet not loaded
 */
export const createClusterIcon = (count, status) => {
  if (!window.L) return null;

  const L = window.L;
  const color = getStatusColorHex(status);

  // Reduce size for very large clusters - more conservative sizing
  let size;
  if (count <= 10) {
    size = mathUtils.max(count * 2, 24); // Normal sizing for small clusters
  } else if (count <= 50) {
    size = mathUtils.min(count * 1.5, 45); // Reduced growth for medium clusters
  } else if (count <= 100) {
    size = mathUtils.min(count * 1.0, 50); // Even more reduced for large clusters
  } else {
    size = mathUtils.min(count * 0.5, 55); // Minimal growth for very large clusters
  }

  // Ensure minimum and maximum bounds
  size = mathUtils.max(mathUtils.min(size, 55), 24);
  const fontSize = mathUtils.max(mathUtils.min(size / 2.5, 14), 10); // Reduced max font size

  return L.divIcon({
    html: `<div style="
      background-color: ${color};
      width: ${size}px;
      height: ${size}px;
      border-radius: 50%;
      border: 3px solid white;
      box-shadow: 0 2px 6px rgba(0,0,0,0.3);
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-weight: bold;
      font-size: ${fontSize}px;
      text-shadow: 0 1px 2px rgba(0,0,0,0.5);
      transition: all 0.3s ease-in-out;
      transform-origin: center;
    ">${count}</div>`,
    className: "custom-cluster-marker",
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
};

/**
 * Calculates distance between two coordinates
 * @param {number} lat1 - First latitude
 * @param {number} lng1 - First longitude
 * @param {number} lat2 - Second latitude
 * @param {number} lng2 - Second longitude
 * @returns {number} Distance in degrees
 */
export const calculateDistance = (lat1, lng1, lat2, lng2) => {
  const dLat = lat2 - lat1;
  const dLng = lng2 - lng1;
  return Math.sqrt(dLat * dLat + dLng * dLng);
};

/**
 * Optimized clustering using spatial indexing for better performance
 * Uses a grid-based approach to reduce O(n²) complexity to O(n)
 * @param {Array} compressors - Array of compressor data with coordinates
 * @param {number} zoom - Current zoom level
 * @param {Object} dataAnalysis - Data analysis results
 * @returns {Array} Array of clusters with optimized clustering applied
 */
const applyOptimizedClustering = (compressors, zoom, dataAnalysis) => {
  if (!compressors || compressors.length === 0) return [];

  // Get base threshold
  const baseThreshold = dataAnalysis
    ? getDataDependentThreshold(zoom, dataAnalysis)
    : getClusteringThreshold(zoom);

  // Calculate zoom threshold using a more efficient lookup
  const zoomMultipliers = [
    8.0, 6.0, 4.0, 1.8, 1.6, 1.4, 1.2, 1.0, 0.8, 0.6, 0.5, 0.4, 0.3, 0.25, 0.2,
    0.15, 0.12, 0.1, 0.08,
  ];

  const zoomIndex = Math.min(Math.max(zoom - 1, 0), zoomMultipliers.length - 1);
  const zoomThreshold = baseThreshold * zoomMultipliers[zoomIndex];

  // For very large datasets, use spatial grid for O(n) clustering
  if (compressors.length > 100) {
    return applySpatialGridClustering(compressors, zoomThreshold);
  }

  // For smaller datasets, use optimized brute force
  return applyBruteForceClustering(compressors, zoomThreshold);
};

/**
 * Spatial grid-based clustering for large datasets
 * @param {Array} compressors - Array of compressor data
 * @param {number} threshold - Clustering threshold
 * @returns {Array} Array of clusters
 */
const applySpatialGridClustering = (compressors, threshold) => {
  const clusters = [];
  const processed = new Set();

  // Create spatial grid
  const gridSize = threshold * 2; // Grid cell size
  const grid = new Map();

  // Assign compressors to grid cells
  compressors.forEach((compressor, index) => {
    const gridX = Math.floor(compressor.lat / gridSize);
    const gridY = Math.floor(compressor.lng / gridSize);
    const key = gridX + "," + gridY;

    if (!grid.has(key)) {
      grid.set(key, []);
    }
    grid.get(key).push({ compressor, index });
  });

  // Process each grid cell
  for (const [key, cellCompressors] of grid) {
    for (let i = 0; i < cellCompressors.length; i++) {
      const compressor = cellCompressors[i].compressor;
      const index = cellCompressors[i].index;
      if (processed.has(index)) continue;

      const cluster = {
        compressors: [compressor],
        center: [compressor.lat, compressor.lng],
        status: compressor.status,
      };

      // Check neighboring grid cells for nearby compressors
      const [gridX, gridY] = key.split(",").map(Number);

      for (let dx = -1; dx <= 1; dx++) {
        for (let dy = -1; dy <= 1; dy++) {
          const neighborKey = gridX + dx + "," + (gridY + dy);
          const neighborCell = grid.get(neighborKey);

          if (neighborCell) {
            for (let j = 0; j < neighborCell.length; j++) {
              const otherCompressor = neighborCell[j].compressor;
              const otherIndex = neighborCell[j].index;
              if (otherIndex === index || processed.has(otherIndex)) continue;

              const distance = calculateDistance(
                compressor.lat,
                compressor.lng,
                otherCompressor.lat,
                otherCompressor.lng
              );

              if (distance <= threshold) {
                cluster.compressors.push(otherCompressor);
                processed.add(otherIndex);
              }
            }
          }
        }
      }

      // Calculate cluster center and status
      if (cluster.compressors.length > 1) {
        const totalLat = cluster.compressors.reduce(
          (sum, comp) => sum + comp.lat,
          0
        );
        const totalLng = cluster.compressors.reduce(
          (sum, comp) => sum + comp.lng,
          0
        );
        cluster.center = [
          totalLat / cluster.compressors.length,
          totalLng / cluster.compressors.length,
        ];

        // Determine cluster status (prioritize more critical statuses)
        cluster.status = cluster.compressors.reduce((prev, curr) => {
          const prevPriority = getStatusPriority(prev) || 0;
          const currPriority = getStatusPriority(curr.status) || 0;
          return currPriority > prevPriority ? curr.status : prev;
        });
      }

      processed.add(index);
      clusters.push(cluster);
    }
  }

  return clusters;
};

/**
 * Optimized brute force clustering for smaller datasets
 * @param {Array} compressors - Array of compressor data
 * @param {number} threshold - Clustering threshold
 * @returns {Array} Array of clusters
 */
const applyBruteForceClustering = (compressors, threshold) => {
  const clusters = [];
  const processed = new Set();

  compressors.forEach((compressor, index) => {
    if (processed.has(index)) return;

    const cluster = {
      compressors: [compressor],
      center: [compressor.lat, compressor.lng],
      status: compressor.status,
    };

    // Find nearby compressors
    for (
      let otherIndex = index + 1;
      otherIndex < compressors.length;
      otherIndex++
    ) {
      if (processed.has(otherIndex)) continue;

      const otherCompressor = compressors[otherIndex];
      const distance = calculateDistance(
        compressor.lat,
        compressor.lng,
        otherCompressor.lat,
        otherCompressor.lng
      );

      if (distance <= threshold) {
        cluster.compressors.push(otherCompressor);
        processed.add(otherIndex);
      }
    }

    // Calculate cluster center and status
    if (cluster.compressors.length > 1) {
      const totalLat = cluster.compressors.reduce(
        (sum, comp) => sum + comp.lat,
        0
      );
      const totalLng = cluster.compressors.reduce(
        (sum, comp) => sum + comp.lng,
        0
      );
      cluster.center = [
        totalLat / cluster.compressors.length,
        totalLng / cluster.compressors.length,
      ];

      // Determine cluster status (prioritize more critical statuses)
      cluster.status = cluster.compressors.reduce((prev, curr) => {
        const prevPriority = getStatusPriority(prev) || 0;
        const currPriority = getStatusPriority(curr.status) || 0;
        return currPriority > prevPriority ? curr.status : prev;
      });
    }

    processed.add(index);
    clusters.push(cluster);
  });

  return clusters;
};

/**
 * Clusters nearby compressors based on zoom level and data characteristics
 * Uses optimized clustering: spatial grid for large datasets, brute force for small datasets
 * @param {Array} compressors - Array of compressor data with coordinates
 * @param {number} zoom - Current zoom level
 * @param {boolean} disableClustering - Whether to disable clustering
 * @param {Object} dataAnalysis - Data analysis results (optional)
 * @returns {Array} Array of clusters, each containing compressors and center coordinates
 */
export const clusterCompressors = (
  compressors,
  zoom = 5,
  disableClustering = false,
  dataAnalysis = null
) => {
  // If clustering is disabled, return each compressor as an individual cluster
  if (disableClustering) {
    return compressors.map((compressor) => ({
      compressors: [compressor],
      center: [compressor.lat, compressor.lng],
      status: compressor.status,
    }));
  }

  // Use optimized clustering based on dataset size and zoom level
  return applyOptimizedClustering(compressors, zoom, dataAnalysis);
};
